import React, { useState, useEffect, useRef, useMemo } from 'react';

// ─── INTERSECTION HOOK ────────────────────────────────────────────────────────
function useVisible(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── DEMO 1: BUSINESS DNA ─────────────────────────────────────────────────────
function DnaDemo({ active }) {
  const fields = [
    { key: 'name',     label: 'Business Name',    value: 'Bloom Bakery Co.',                           color: '#60a5fa' },
    { key: 'persona',  label: 'Ideal Customer',   value: 'Emma — Busy working mum, age 28–42',         color: '#34d399' },
    { key: 'position', label: 'Your Unique Edge',  value: 'Artisan quality at everyday prices',         color: '#a78bfa' },
  ];
  const [texts,     setTexts]     = useState({ name: '', persona: '', position: '' });
  const [activeIdx, setActiveIdx] = useState(0);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    if (!active) { setTexts({ name: '', persona: '', position: '' }); setActiveIdx(0); setDone(false); return; }
    let cancel = false;
    const run = async () => {
      while (!cancel) {
        setTexts({ name: '', persona: '', position: '' });
        setDone(false);
        for (let fi = 0; fi < fields.length; fi++) {
          if (cancel) return;
          setActiveIdx(fi);
          const { key, value } = fields[fi];
          for (let ci = 0; ci <= value.length; ci++) {
            if (cancel) return;
            setTexts(p => ({ ...p, [key]: value.slice(0, ci) }));
            await new Promise(r => setTimeout(r, 30 + Math.random() * 20));
          }
          await new Promise(r => setTimeout(r, 420));
        }
        setDone(true);
        setActiveIdx(-1);
        await new Promise(r => setTimeout(r, 3000));
      }
    };
    run();
    return () => { cancel = true; };
  }, [active]); // eslint-disable-line

  return (
    <div className="space-y-2.5 mt-5">
      {fields.map((f, i) => (
        <div key={f.key}
          className="p-3.5 rounded-xl border transition-all duration-300"
          style={{
            background: activeIdx === i && !done ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
            borderColor: activeIdx === i && !done ? f.color + '45' : 'rgba(255,255,255,0.07)',
          }}
        >
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: f.color }}>
            {f.label}
          </div>
          <div className="text-sm font-semibold text-white/90 min-h-[19px] flex items-center">
            <span>{texts[f.key]}</span>
            {activeIdx === i && !done && (
              <span className="inline-block w-[2px] h-4 ml-0.5 rounded-sm animate-pulse" style={{ background: f.color }} />
            )}
          </div>
        </div>
      ))}
      <div className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all duration-500 ${done ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'rgba(16,185,129,0.07)', borderColor: 'rgba(16,185,129,0.18)' }}
      >
        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <span className="text-xs text-emerald-400 font-semibold">Business DNA captured — generating your 30-day strategy...</span>
      </div>
    </div>
  );
}

// ─── DEMO 2: 30-DAY STRATEGY ──────────────────────────────────────────────────
function StrategyDemo({ active }) {
  const [filled, setFilled] = useState(0);
  const TOTAL = 30;
  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

  useEffect(() => {
    if (!active) { setFilled(0); return; }
    setFilled(0);
    let count = 0;
    const iv = setInterval(() => {
      count++;
      setFilled(count);
      if (count >= TOTAL) { clearInterval(iv); setTimeout(() => setFilled(0), 2200); }
    }, 75);
    return () => clearInterval(iv);
  }, [active]);

  const types = ['📸 Instagram Post', '👥 Facebook Post', '🎬 Short Video', '📋 Story'];

  return (
    <div className="mt-5">
      <div className="grid grid-cols-6 gap-1.5 mb-3">
        {Array.from({ length: TOTAL }, (_, i) => (
          <div key={i}
            className="h-8 rounded-lg flex items-center justify-center text-[10px] font-bold transition-all duration-300"
            style={{
              background: i < filled ? COLORS[i % COLORS.length] + '22' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${i < filled ? COLORS[i % COLORS.length] + '50' : 'rgba(255,255,255,0.06)'}`,
              color: i < filled ? COLORS[i % COLORS.length] : 'rgba(255,255,255,0.15)',
              transform: i < filled ? 'scale(1.05)' : 'scale(1)',
              transitionDelay: `${i * 8}ms`,
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs mt-2 mb-3">
        <span className="text-slate-500">Days planned</span>
        <span className="font-bold text-blue-400">{filled} / {TOTAL}</span>
      </div>
      <div className="space-y-1.5">
        {types.map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-[11px] text-slate-500">
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: COLORS[i] }} />
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DEMO 3: AI CONTENT GENERATION ───────────────────────────────────────────
function ContentDemo({ active }) {
  const examples = [
    {
      caption: "✨ Running a small business is tough — but your marketing doesn't have to be. Let AI handle your content so you can focus on what you love most.",
      hashtags: '#SmallBusiness #AIMarketing #ContentCreator #Entrepreneur #DigitalMarketing',
      platform: 'Instagram', day: 'Day 7',
    },
    {
      caption: "📱 Consistency is the #1 secret to growing your audience. 30 days of posts, planned and written for you. Never stare at a blank screen again.",
      hashtags: '#SocialMediaTips #BusinessOwner #MarketingStrategy #ContentCalendar #Growth',
      platform: 'Facebook', day: 'Day 14',
    },
  ];

  const [idx, setIdx]         = useState(0);
  const [caption, setCaption] = useState('');
  const [tags, setTags]       = useState('');
  const [phase, setPhase]     = useState('caption');

  useEffect(() => {
    if (!active) { setCaption(''); setTags(''); return; }
    let cancel = false;
    const run = async () => {
      while (!cancel) {
        const ex = examples[idx % examples.length];
        setCaption(''); setTags(''); setPhase('caption');
        for (let i = 0; i <= ex.caption.length; i++) {
          if (cancel) return;
          setCaption(ex.caption.slice(0, i));
          await new Promise(r => setTimeout(r, 10 + Math.random() * 8));
        }
        setPhase('hashtags');
        await new Promise(r => setTimeout(r, 280));
        for (let i = 0; i <= ex.hashtags.length; i++) {
          if (cancel) return;
          setTags(ex.hashtags.slice(0, i));
          await new Promise(r => setTimeout(r, 13));
        }
        await new Promise(r => setTimeout(r, 2600));
        if (!cancel) setIdx(p => p + 1);
      }
    };
    run();
    return () => { cancel = true; };
  }, [active, idx]); // eslint-disable-line

  const ex = examples[idx % examples.length];

  return (
    <div className="mt-4 rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
      <div className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.025)' }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[11px] text-slate-400 font-medium">AI Writing — {ex.platform}</span>
        </div>
        <span className="text-[10px] text-slate-600 px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.05)' }}>{ex.day}</span>
      </div>
      <div className="p-3">
        <p className="text-xs text-white/80 leading-relaxed mb-3 min-h-[64px]">
          {caption}
          {phase === 'caption' && caption.length > 0 && (
            <span className="inline-block w-[2px] h-3.5 bg-white/40 ml-0.5 animate-pulse rounded-sm" />
          )}
        </p>
        <p className="text-[11px] font-medium leading-relaxed" style={{ color: '#60a5fa' }}>
          {tags}
          {phase === 'hashtags' && tags.length > 0 && (
            <span className="inline-block w-[2px] h-3 ml-0.5 animate-pulse rounded-sm" style={{ background: '#60a5fa' }} />
          )}
        </p>
      </div>
    </div>
  );
}

// ─── DEMO 4: DESIGN STUDIO ────────────────────────────────────────────────────
function DesignDemo({ active }) {
  const templates = [
    { name: 'Clean Minimal',   bg: '#1a3264', accent: '#3b82f6' },
    { name: 'Bold Overlay',    bg: '#0d3d2e', accent: '#10b981' },
    { name: 'Split Layout',    bg: '#2d0f55', accent: '#8b5cf6' },
    { name: 'Top Band',        bg: '#3d1a03', accent: '#f59e0b' },
    { name: 'Quote Card',      bg: '#3d0a14', accent: '#f43f5e' },
    { name: 'Event Promo',     bg: '#053040', accent: '#06b6d4' },
    { name: 'Product Hero',    bg: '#141e38', accent: '#818cf8' },
    { name: 'Bold Modern',     bg: '#053060', accent: '#38bdf8' },
  ];

  const [selected, setSelected]           = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    if (!active) { setSelected(0); return; }
    const iv = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => { setSelected(p => (p + 1) % templates.length); setTransitioning(false); }, 320);
    }, 2200);
    return () => clearInterval(iv);
  }, [active]); // eslint-disable-line

  const t = templates[selected];

  return (
    <div className="mt-4 space-y-3">
      {/* Large preview — social post with image placeholder */}
      <div className="relative w-full rounded-xl overflow-hidden"
        style={{ background: t.bg, aspectRatio: '4/3', opacity: transitioning ? 0 : 1, transition: 'opacity 0.32s ease' }}
      >
        {/* Image placeholder area (top 55%) */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-center"
          style={{ height: '55%', background: 'rgba(0,0,0,0.28)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col items-center gap-1.5 opacity-40">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[8px] text-white font-medium tracking-wider uppercase">Photo</span>
          </div>
        </div>

        {/* Text content area (bottom 45%) */}
        <div className="absolute bottom-0 left-0 right-0 p-3" style={{ height: '45%' }}>
          <div className="h-[2px] w-6 rounded-full mb-2" style={{ background: t.accent }} />
          <div className="h-3.5 rounded mb-1.5" style={{ background: 'rgba(255,255,255,0.75)', width: '72%' }} />
          <div className="h-2.5 rounded mb-3" style={{ background: 'rgba(255,255,255,0.30)', width: '52%' }} />
          <div className="h-6 w-20 rounded flex items-center justify-center" style={{ background: t.accent }}>
            <div className="h-1.5 w-10 rounded" style={{ background: 'rgba(255,255,255,0.7)' }} />
          </div>
        </div>

        {/* Template name + AI badge */}
        <div className="absolute top-2 right-2 flex items-center gap-1 text-[8px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)' }}>
          ✨ AI
        </div>
        <div className="absolute bottom-2.5 right-2.5">
          <span className="text-[8px] text-white/30">{t.name}</span>
        </div>
      </div>

      {/* Template selector thumbnails */}
      <div className="grid grid-cols-8 gap-1.5">
        {templates.map((tmpl, i) => (
          <button key={i} onClick={() => setSelected(i)}
            className="aspect-square rounded-lg transition-all duration-200"
            style={{
              background: tmpl.bg,
              opacity: selected === i ? 1 : 0.35,
              transform: selected === i ? 'scale(1.12)' : 'scale(1)',
              border: selected === i ? `1.5px solid ${tmpl.accent}` : '1.5px solid rgba(255,255,255,0.06)',
            }}
          />
        ))}
      </div>

      {/* Capability row */}
      <div className="flex items-center gap-3 flex-wrap text-[10px] text-slate-500 pt-0.5">
        {['8 templates', 'AI images', '14 text styles', 'Logo remove-bg'].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full" style={{ background: ['#3b82f6','#10b981','#a78bfa','#f59e0b'][i] }} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DEMO 5: VIDEO EDITOR TIMELINE ───────────────────────────────────────────
function VideoDemo({ active }) {
  const [playhead, setPlayhead] = useState(0); // 0–100
  const [playing, setPlaying]   = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const clips = [
    { label: 'Intro', pct: 22, color: '#3b82f6' },
    { label: 'Hook',  pct: 18, color: '#10b981' },
    { label: 'Main',  pct: 38, color: '#8b5cf6' },
    { label: 'CTA',   pct: 22, color: '#f59e0b' },
  ];

  const waveHeights = useMemo(() =>
    Array.from({ length: 48 }, (_, i) =>
      Math.max(18, 28 + Math.sin(i * 0.72) * 22 + Math.sin(i * 1.45) * 12 + Math.sin(i * 2.1) * 6)
    ), []
  );

  useEffect(() => {
    if (!active) { setPlayhead(0); setPlaying(false); setShowOverlay(false); return; }
    let cancel = false;
    const run = async () => {
      while (!cancel) {
        setPlayhead(0);
        setPlaying(true);
        setShowOverlay(false);
        const steps = 80;
        for (let i = 0; i <= steps; i++) {
          if (cancel) return;
          setPlayhead((i / steps) * 100);
          if (i === 32) setShowOverlay(true);
          await new Promise(r => setTimeout(r, 55));
        }
        setPlaying(false);
        await new Promise(r => setTimeout(r, 1800));
        setShowOverlay(false);
        setPlayhead(0);
        await new Promise(r => setTimeout(r, 600));
      }
    };
    run();
    return () => { cancel = true; };
  }, [active]);

  const totalSec = 62;
  const currentSec = Math.floor((playhead / 100) * totalSec);
  const timecode = `0:${String(currentSec).padStart(2, '0')} / 1:02`;

  // Build clip track positions
  let acc = 0;
  const clipPos = clips.map(c => { const s = acc; acc += c.pct; return { ...c, start: s }; });

  return (
    <div className="mt-4 space-y-2.5">
      {/* Video preview */}
      <div className="relative rounded-xl overflow-hidden"
        style={{ height: '110px', background: '#08101e', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Flat dark scene bg */}
        <div className="absolute inset-0" style={{ background: '#08101e' }} />

        {/* Abstract scene elements — flat tinted rects */}
        <div className="absolute top-4 left-8 w-24 h-16 rounded-lg"
          style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.12)' }} />
        <div className="absolute top-6 right-10 w-16 h-12 rounded-lg"
          style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.1)' }} />

        {/* REC indicator */}
        {playing && (
          <div className="absolute top-2.5 left-3 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[9px] font-bold text-white/50 tracking-wider">REC</span>
          </div>
        )}

        {/* Timecode */}
        <div className="absolute top-2.5 right-3">
          <span className="text-[9px] font-mono text-white/35">{timecode}</span>
        </div>

        {/* Text overlay — appears mid-video */}
        {showOverlay && (
          <div className="absolute inset-x-3 bottom-3 transition-opacity duration-500" style={{ opacity: showOverlay ? 1 : 0 }}>
            <div className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5"
              style={{ background: 'rgba(0,0,0,0.72)', border: '1px solid rgba(255,255,255,0.14)' }}>
              <div className="w-[3px] h-4 rounded-sm flex-shrink-0" style={{ background: '#3b82f6' }} />
              <span className="text-[10px] font-bold text-white">Your Brand Story</span>
              {/* Edit handle */}
              <div className="w-3.5 h-3.5 rounded border border-white/25 flex items-center justify-center flex-shrink-0 ml-1">
                <div className="w-[2px] h-2.5 rounded-sm" style={{ background: 'rgba(255,255,255,0.4)' }} />
              </div>
            </div>
          </div>
        )}

        {/* AI Edit badge */}
        <div className="absolute bottom-2.5 right-3 flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(59,130,246,0.18)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
          ✨ AI Edit
        </div>
      </div>

      {/* Timeline editor */}
      <div className="rounded-xl overflow-hidden"
        style={{ background: '#060d1a', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-3 pt-2.5 pb-2">
          {/* Video track */}
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] uppercase tracking-wider text-slate-600 w-8 flex-shrink-0">VID</span>
            <div className="relative flex-1 h-8 rounded overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.04)' }}>
              {clipPos.map((clip, i) => (
                <div key={i} className="absolute h-full flex items-center justify-center"
                  style={{
                    left: `${clip.start}%`,
                    width: `${clip.pct}%`,
                    background: clip.color + '22',
                    borderLeft: i > 0 ? '1px solid rgba(0,0,0,0.4)' : 'none',
                  }}>
                  <span className="text-[8px] font-bold" style={{ color: clip.color }}>{clip.label}</span>
                </div>
              ))}
              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-px z-10"
                style={{ left: `${playhead}%`, background: 'rgba(255,255,255,0.9)' }} />
            </div>
          </div>

          {/* Audio waveform track */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] uppercase tracking-wider text-slate-600 w-8 flex-shrink-0">AUD</span>
            <div className="relative flex-1 h-5 flex items-center gap-px overflow-hidden rounded"
              style={{ background: 'rgba(255,255,255,0.025)' }}>
              {waveHeights.map((h, i) => {
                const isCurrent = (i / waveHeights.length) * 100 <= playhead;
                return (
                  <div key={i} className="flex-1 self-center rounded-sm"
                    style={{
                      height: `${h}%`,
                      background: isCurrent ? '#10b981' : 'rgba(16,185,129,0.18)',
                      transition: 'background 0.1s',
                    }}
                  />
                );
              })}
              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-px z-10"
                style={{ left: `${playhead}%`, background: 'rgba(255,255,255,0.7)' }} />
            </div>
          </div>
        </div>

        {/* Transport + toolbar */}
        <div className="flex items-center gap-1.5 px-3 pb-2.5 flex-wrap">
          {[
            { icon: playing ? '⏸' : '▶', label: 'Play' },
            { icon: '✂', label: 'Cut' },
            { icon: 'T', label: 'Text' },
            { icon: '🎵', label: 'Music' },
          ].map((btn, i) => (
            <div key={i}
              className="flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1.5 rounded-lg cursor-default"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(203,213,225,0.65)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <span>{btn.icon}</span>
              <span>{btn.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-lg ml-auto cursor-default"
            style={{ background: 'rgba(59,130,246,0.14)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.22)' }}>
            ✨ AI Cut
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DEMO 6: INSTAGRAM + FACEBOOK PUBLISHING ─────────────────────────────────
function PublishDemo({ active }) {
  // phases: connect → ig_connecting → ig_done → fb_connecting → connected → publishing → live
  const [phase, setPhase] = useState('connect');

  useEffect(() => {
    if (!active) { setPhase('connect'); return; }
    let cancel = false;
    const run = async () => {
      while (!cancel) {
        setPhase('connect');
        await new Promise(r => setTimeout(r, 1400));
        if (cancel) return;
        setPhase('ig_connecting');
        await new Promise(r => setTimeout(r, 1100));
        if (cancel) return;
        setPhase('ig_done');
        await new Promise(r => setTimeout(r, 700));
        if (cancel) return;
        setPhase('fb_connecting');
        await new Promise(r => setTimeout(r, 1000));
        if (cancel) return;
        setPhase('connected');
        await new Promise(r => setTimeout(r, 1300));
        if (cancel) return;
        setPhase('publishing');
        await new Promise(r => setTimeout(r, 2200));
        if (cancel) return;
        setPhase('live');
        await new Promise(r => setTimeout(r, 3000));
        if (cancel) return;
        setPhase('idle');
        await new Promise(r => setTimeout(r, 400));
      }
    };
    run();
    return () => { cancel = true; };
  }, [active]);

  const igConnected = ['ig_done','fb_connecting','connected','publishing','live'].includes(phase);
  const fbConnected = ['connected','publishing','live'].includes(phase);
  const isLive      = phase === 'live';

  const platforms = [
    {
      name: 'Instagram',
      letter: 'I',
      gradient: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)',
      connected: igConnected,
      connecting: phase === 'ig_connecting',
    },
    {
      name: 'Facebook',
      letter: 'f',
      gradient: 'linear-gradient(135deg,#1877f2,#0c5fc0)',
      connected: fbConnected,
      connecting: phase === 'fb_connecting',
    },
  ];

  return (
    <div className="mt-4 space-y-3">
      {/* Post preview — simulates a designed social post */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.09)' }}>
        {/* Profile header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.025)' }}>
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
            style={{ background: '#2563eb' }}>B</div>
          <span className="text-xs font-semibold text-white flex-1">Bloom Bakery Co.</span>
          {isLive && (
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] text-emerald-400 font-bold">Live</span>
            </div>
          )}
        </div>

        {/* Image area — placeholder for designed post photo */}
        <div className="relative" style={{ height: '88px', background: '#0a1220' }}>
          {/* Placeholder image slot */}
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ border: '1px dashed rgba(255,255,255,0.1)', margin: '6px', borderRadius: '6px' }}>
            <div className="flex flex-col items-center gap-1 opacity-30">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-[8px] text-white uppercase tracking-wider">Image placeholder</span>
            </div>
          </div>

          {/* Text overlay at bottom */}
          <div className="absolute bottom-2 left-3 right-3" style={{ zIndex: 2 }}>
            <div className="h-2.5 rounded mb-1" style={{ background: 'rgba(255,255,255,0.55)', width: '68%' }} />
            <div className="h-2 rounded" style={{ background: 'rgba(255,255,255,0.22)', width: '48%' }} />
          </div>

          {/* Publishing overlay */}
          {phase === 'publishing' && (
            <div className="absolute inset-0 flex items-center justify-center rounded"
              style={{ background: 'rgba(6,14,26,0.72)', backdropFilter: 'blur(2px)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-blue-300 font-semibold">Publishing to all platforms...</span>
              </div>
            </div>
          )}
        </div>

        {/* Caption */}
        <div className="px-3 py-2">
          <p className="text-[10px] leading-snug" style={{ color: 'rgba(148,163,184,0.6)' }}>
            Artisan quality at everyday prices ✨&nbsp;
            <span style={{ color: '#60a5fa' }}>#SmallBusiness #BakerLife #AIMarketing</span>
          </p>
        </div>
      </div>

      {/* Platform rows */}
      {platforms.map((p) => (
        <div key={p.name}
          className="flex items-center gap-3 p-3 rounded-xl border transition-all duration-400"
          style={{
            background: p.connected ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.02)',
            borderColor: p.connected ? 'rgba(16,185,129,0.16)' : 'rgba(255,255,255,0.07)',
          }}
        >
          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ background: p.gradient }}>
            {p.letter}
          </div>
          <span className="text-sm font-semibold text-white/90 flex-1">{p.name}</span>

          {/* State */}
          {phase === 'connect' && !p.connected ? (
            <div className="text-[10px] font-semibold px-2.5 py-1 rounded-lg cursor-default"
              style={{ background: 'rgba(59,130,246,0.1)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.22)' }}>
              Connect
            </div>
          ) : p.connecting ? (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 border-[1.5px] border-slate-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-[10px] text-slate-400">Connecting...</span>
            </div>
          ) : p.connected ? (
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center">
                <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-[10px] font-semibold" style={{ color: isLive ? '#4ade80' : '#34d399' }}>
                {isLive ? 'Posted ✓' : 'Connected'}
              </span>
            </div>
          ) : null}
        </div>
      ))}

      {/* Live engagement metrics */}
      {isLive && (
        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          {[
            { icon: '❤️', val: '32',   lbl: 'Likes'    },
            { icon: '💬', val: '8',    lbl: 'Comments' },
            { icon: '📤', val: '14',   lbl: 'Shares'   },
            { icon: '👁️', val: '1.4K', lbl: 'Reach'    },
          ].map((m) => (
            <div key={m.lbl} className="text-center">
              <div className="text-sm">{m.icon}</div>
              <div className="text-xs font-bold text-white">{m.val}</div>
              <div className="text-[9px] text-slate-600">{m.lbl}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DEMO 7: ANALYTICS & STREAK ───────────────────────────────────────────────
function AnalyticsDemo({ active }) {
  const bars = [
    { day: 'Mon', h: 62, color: '#3b82f6' },
    { day: 'Tue', h: 85, color: '#10b981' },
    { day: 'Wed', h: 51, color: '#3b82f6' },
    { day: 'Thu', h: 94, color: '#8b5cf6' },
    { day: 'Fri', h: 74, color: '#10b981' },
    { day: 'Sat', h: 88, color: '#f59e0b' },
    { day: 'Sun', h: 67, color: '#3b82f6' },
  ];
  const [heights, setHeights] = useState(bars.map(() => 0));

  useEffect(() => {
    if (!active) { setHeights(bars.map(() => 0)); return; }
    const t = setTimeout(() => setHeights(bars.map(b => b.h)), 300);
    return () => clearTimeout(t);
  }, [active]); // eslint-disable-line

  const metrics = [
    { label: 'Posts Published', value: '26/30',  color: '#3b82f6' },
    { label: 'Avg Engagement',  value: '4.8%',   color: '#10b981' },
    { label: 'Total Reach',     value: '15.6K',  color: '#8b5cf6' },
    { label: 'Posting Streak',  value: '🔥 14d', color: '#f59e0b' },
  ];

  return (
    <div className="space-y-5 mt-2">
      {/* Bar chart */}
      <div className="flex items-end gap-2" style={{ height: '108px' }}>
        {bars.map((b, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
            <div className="w-full relative rounded-t-md overflow-hidden" style={{ height: '88px', background: 'rgba(255,255,255,0.04)' }}>
              <div className="absolute bottom-0 w-full rounded-t-md transition-all duration-900 ease-out"
                style={{
                  height: `${heights[i]}%`,
                  background: `linear-gradient(to top, ${b.color}, ${b.color}70)`,
                  transitionDelay: `${i * 85}ms`,
                }}
              />
            </div>
            <span className="text-[9px] font-medium" style={{ color: 'rgba(100,116,139,0.75)' }}>{b.day}</span>
          </div>
        ))}
      </div>

      {/* Metric pills */}
      <div className="grid grid-cols-4 gap-2">
        {metrics.map((m, i) => (
          <div key={i} className="p-2.5 rounded-xl text-center"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="text-sm font-extrabold mb-0.5" style={{ color: m.color }}>{m.value}</div>
            <div className="text-[9px] leading-tight" style={{ color: 'rgba(100,116,139,0.85)' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* PDF Report */}
      <div className="flex items-center gap-3 p-3 rounded-xl border"
        style={{ background: 'rgba(37,99,235,0.07)', borderColor: 'rgba(37,99,235,0.18)' }}>
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <div className="text-xs font-bold text-white">Campaign Report Ready</div>
          <div className="text-[10px]" style={{ color: 'rgba(148,163,184,0.65)' }}>Download your 30-day PDF report</div>
        </div>
        <div className="ml-auto text-[10px] font-bold text-blue-400 border border-blue-500/30 px-2.5 py-1 rounded-lg">PDF ↓</div>
      </div>
    </div>
  );
}

// ─── FEATURE CARD WRAPPER ─────────────────────────────────────────────────────
function FeatureCard({ tag, title, description, badge, accentColor, Demo, className = '', index = 0, children }) {
  const [ref, visible] = useVisible(0.1);

  const accents = {
    blue:   { tagBg: 'rgba(59,130,246,0.10)',  tagBorder: 'rgba(59,130,246,0.22)', tagColor: '#60a5fa', dot: '#3b82f6',  gridColor: 'rgba(59,130,246,0.30)'  },
    green:  { tagBg: 'rgba(16,185,129,0.10)',  tagBorder: 'rgba(16,185,129,0.22)', tagColor: '#34d399', dot: '#10b981',  gridColor: 'rgba(16,185,129,0.30)'  },
    purple: { tagBg: 'rgba(139,92,246,0.10)',  tagBorder: 'rgba(139,92,246,0.22)', tagColor: '#a78bfa', dot: '#8b5cf6',  gridColor: 'rgba(139,92,246,0.30)' },
    amber:  { tagBg: 'rgba(245,158,11,0.10)',  tagBorder: 'rgba(245,158,11,0.22)', tagColor: '#fbbf24', dot: '#f59e0b',  gridColor: 'rgba(245,158,11,0.30)' },
  };
  const ac = accents[accentColor] || accents.blue;

  return (
    <div ref={ref}
      className={`relative rounded-2xl overflow-hidden p-5 transition-all duration-700 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{
        transitionDelay: `${index * 50}ms`,
        background: '#0a1526',
        border: '1px solid rgba(255,255,255,0.09)',
      }}
    >
      {/* Corner micro-grid accent */}
      <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${ac.gridColor} 1px, transparent 1px), linear-gradient(90deg, ${ac.gridColor} 1px, transparent 1px)`,
          backgroundSize: '11px 11px',
          opacity: 0.14,
          maskImage: 'radial-gradient(circle at top right, black 15%, transparent 65%)',
          WebkitMaskImage: 'radial-gradient(circle at top right, black 15%, transparent 65%)',
        }}
      />

      {/* Card header: tag left — badge right */}
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1"
          style={{ background: ac.tagBg, border: `1px solid ${ac.tagBorder}`, color: ac.tagColor }}>
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: ac.dot }} />
          {tag}
        </div>
        {badge && (
          <span className="text-[10px] font-semibold text-slate-500 tracking-wide hidden sm:block">
            {badge.split('·')[0].trim()}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-extrabold text-white leading-snug mb-2">{title}</h3>

      {/* Description */}
      <p className="text-sm leading-relaxed" style={{ color: 'rgba(148,163,184,0.80)' }}>{description}</p>

      {/* Live Demo */}
      {Demo && <Demo active={visible} />}
      {children}
    </div>
  );
}

// ─── JOURNEY STEP ─────────────────────────────────────────────────────────────
function JourneyStep({ label, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="text-[11px] font-semibold whitespace-nowrap" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

// ─── MAIN SECTION ─────────────────────────────────────────────────────────────
export default function FeaturesSection() {
  const [headerRef, headerVisible] = useVisible(0.2);

  const journeySteps = [
    { label: 'Business DNA',  color: '#60a5fa' },
    { label: 'AI Strategy',   color: '#34d399' },
    { label: 'Content',       color: '#a78bfa' },
    { label: 'Design Studio', color: '#60a5fa' },
    { label: 'Video',         color: '#fbbf24' },
    { label: 'Publish',       color: '#34d399' },
    { label: 'Analytics',     color: '#60a5fa' },
  ];

  return (
    <section className="py-24 overflow-hidden relative" style={{ background: '#060e1e' }}>

      {/* Global micro-grid background */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          opacity: 0.018,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

        {/* ── SECTION HEADER ───────────────────────────────────────────────── */}
        <div ref={headerRef}
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          {/* Tag */}
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-7"
            style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)' }}>
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#93c5fd' }}>
              Everything in one platform
            </span>
          </div>

          {/* Headline */}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] mb-5">
            From business brief<br />
            <span style={{ backgroundImage: 'linear-gradient(90deg,#60a5fa 0%,#34d399 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              to published post.
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-lg max-w-2xl mx-auto leading-relaxed mb-8" style={{ color: 'rgba(148,163,184,0.82)' }}>
            We understand your business, generate your 30-day strategy, write your captions,
            design your graphics, guide your video, publish to Instagram &amp; Facebook,
            and track every result — all in one platform.
          </p>

          {/* Journey flow */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            {journeySteps.map((step, i) => (
              <React.Fragment key={i}>
                <JourneyStep label={step.label} color={step.color} />
                {i < journeySteps.length - 1 && (
                  <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'rgba(100,116,139,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── BENTO GRID ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* ROW 1 — Business DNA (2 col) + AI Strategy (1 col) */}
          <FeatureCard
            tag="Step 1 — 2 Minutes"
            title="We understand your business before we touch a single post."
            description="Tell us your business name, who your customers are, and what makes you different. Everything we create is built around your unique story — not a generic template."
            badge="Brand positioning · Target persona · Business goals"
            accentColor="blue"
            Demo={DnaDemo}
            className="lg:col-span-2"
            index={0}
          />

          <FeatureCard
            tag="AI Strategy"
            title="30-day plan. Done while you make a coffee."
            description="SMART goals, channel strategy, and daily post briefs — generated by AI and tailored to your business in minutes."
            badge="30 posts · 2 platforms · Instant"
            accentColor="green"
            Demo={StrategyDemo}
            index={1}
          />

          {/* ROW 2 — Content Gen (1 col) + Design Studio (2 col) */}
          <FeatureCard
            tag="AI Writing"
            title="30 days of captions. Every one unique. All written by AI."
            description="Engaging captions and the right hashtags for every single post — crafted in your brand voice, for your exact audience."
            badge="30 captions + hashtag sets"
            accentColor="purple"
            Demo={ContentDemo}
            index={2}
          />

          <FeatureCard
            tag="Design Studio"
            title="Professional graphics. Zero design skills needed."
            description="8 stunning layout templates, AI image generation, 14 Google Font text styles, and one-click logo background removal. Canva-like power, built right in."
            badge="8 templates · AI images · 14 text styles · Logo remove-bg"
            accentColor="blue"
            Demo={DesignDemo}
            className="lg:col-span-2"
            index={3}
          />

          {/* ROW 3 — Video (1 col) + Publishing (2 col) */}
          <FeatureCard
            tag="Video Creator"
            title="Your first video post. We guide every step of the way."
            description="Brief, script, guided filming checklist, and AI-assisted editing — in a structured 4-step workflow anyone can follow, even on day one."
            badge="Brief → Script → Film → Edit"
            accentColor="amber"
            Demo={VideoDemo}
            index={4}
          />

          <FeatureCard
            tag="Direct Publishing"
            title="One click to Instagram & Facebook. No switching apps."
            description="Connect your accounts once. Then your designed post, AI caption, and hashtags go live with a single tap — no copying, no pasting, no third-party tools."
            badge="Instagram · Facebook · Instant · No third-party tools"
            accentColor="green"
            Demo={PublishDemo}
            className="lg:col-span-2"
            index={5}
          />

          {/* ROW 4 — Analytics (full width) */}
          <FeatureCard
            tag="Campaign Analytics"
            title="See every post. Track every win. Download your report."
            description="Live campaign dashboard shows your posting streak, completion rate, engagement, and total reach. One click downloads your full 30-day PDF campaign report — ready to share with your team or clients."
            badge="Posting streak · Completion rate · Engagement · PDF report"
            accentColor="blue"
            Demo={AnalyticsDemo}
            className="lg:col-span-3"
            index={6}
          />
        </div>

        {/* ── BOTTOM CTA ───────────────────────────────────────────────────── */}
        <div className="mt-16 text-center">
          <p className="text-sm mb-6" style={{ color: 'rgba(100,116,139,0.75)' }}>
            Ready to experience the full platform?
          </p>
          <a
            href="/auth/signup"
            className="inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-xl text-white text-sm transition-all duration-200"
            style={{ background: '#2563eb' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = '#1d4ed8'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = '#2563eb'; }}
          >
            Start Your Free 14-Day Trial
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
          <p className="text-xs mt-3" style={{ color: 'rgba(100,116,139,0.45)' }}>
            No credit card required · Cancel anytime
          </p>
        </div>

      </div>
    </section>
  );
}
